// Camera component that provides camera functionality and image capture capabilities
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Text,
  ActivityIndicator,
  Platform,
  Alert,
  GestureResponderEvent,
} from 'react-native';
import Button from '../common/Button';
import { tensorFlowService } from '../../utils/tensorflow';
import { RGBtoHSV } from '../../utils/colorUtils';
import {
  faceDetectionService,
  FaceDetectionResult,
} from '../../services/FaceDetectionService';
import { useTheme } from '../../hooks/useTheme';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { trackButtonClick, trackAnalyticsEvent } from '../../utils/analytics';

interface CameraComponentProps {
  onImageCaptured: (
    uri: string,
    skinTone?: {
      category: string;
      confidence: number;
      color: string;
      undertone?: string;
    },
    faceFeatures?: string[]
  ) => void; // Callback when image is captured
  onClose: () => void; // Callback to close camera view
  captureOvalOnly?: boolean; // When true, export only the oval crop (virtual drape)
}

// ─── Facial feature regions for scan overlay ─────────────────────────────────
// ox/oy = offset from oval center as fraction of rx/ry
// rw/rh = box size as fraction of rx/ry
// scanT = animation progress (0-1) when the scan line reaches this feature
const SKIN_FEATURES = [
  { label: 'Fine lines', ox: 0.0, oy: -0.62, rw: 0.55, rh: 0.12, scanT: 0.19 },
  { label: 'Eye bags', ox: -0.3, oy: -0.14, rw: 0.22, rh: 0.1, scanT: 0.42 },
  { label: '', ox: 0.3, oy: -0.14, rw: 0.22, rh: 0.1, scanT: 0.42 },
  {
    label: "Crow's feet",
    ox: -0.55,
    oy: -0.1,
    rw: 0.16,
    rh: 0.16,
    scanT: 0.44,
  },
  { label: '', ox: 0.55, oy: -0.1, rw: 0.16, rh: 0.16, scanT: 0.44 },
  { label: 'Pores', ox: -0.1, oy: 0.1, rw: 0.18, rh: 0.22, scanT: 0.56 },
  { label: 'Blemishes', ox: 0.38, oy: 0.2, rw: 0.14, rh: 0.14, scanT: 0.6 },
  {
    label: 'Laugh lines',
    ox: -0.34,
    oy: 0.38,
    rw: 0.12,
    rh: 0.28,
    scanT: 0.65,
  },
  { label: '', ox: 0.34, oy: 0.38, rw: 0.12, rh: 0.28, scanT: 0.68 },
];

const getScanZoneLabel = (pct: number): string => {
  if (pct < 19) return 'Scanning hairline…';
  if (pct < 35) return 'Analyzing forehead…';
  if (pct < 50) return 'Detecting eye region…';
  if (pct < 65) return 'Scanning cheekbones…';
  if (pct < 80) return 'Analyzing laugh lines…';
  if (pct < 92) return 'Scanning chin & jaw…';
  return 'Finalizing analysis…';
};

export const CameraComponent: React.FC<CameraComponentProps> = ({
  onImageCaptured,
  onClose,
  captureOvalOnly = false,
}) => {
  // State management for camera functionality
  const [hasPermission, setHasPermission] = useState<boolean | null>(null); // Camera permission state
  const [isProcessing, setIsProcessing] = useState(false);
  const [scanPhase, setScanPhase] = useState<'idle' | 'scanning' | 'complete'>(
    'idle'
  );
  const [scanPercent, setScanPercent] = useState(0);
  const lastPctRef = useRef(-1);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [currentSkinTone, setCurrentSkinTone] = useState<{
    category: string;
    confidence: number;
    color: string;
    undertone?: string;
  } | null>(null);
  const [, setFaceDetection] = useState<FaceDetectionResult | null>(null);
  const [isFaceDetected, setIsFaceDetected] = useState(false);
  const [isFaceInOval, setIsFaceInOval] = useState(false);
  const isFaceDetectedRef = useRef(false);
  const isFaceInOvalRef = useRef(false);
  useEffect(() => {
    isFaceDetectedRef.current = isFaceDetected;
    isFaceInOvalRef.current = isFaceInOval;
  }, [isFaceDetected, isFaceInOval]);
  const [, setFaceQuality] = useState<{
    brightness: number;
    contrast: number;
    sharpness: number;
    overall: number;
  } | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const { colors } = useTheme();
  const { isMobile } = useResponsiveLayout();
  const analysisInterval = useRef<NodeJS.Timeout | null>(null);
  const faceDetectionInterval = useRef<NodeJS.Timeout | null>(null);
  const scanAnimRef = useRef<number | null>(null);

  // ─── Oval geometry ───────────────────────────────────────────────────────
  const getOvalParams = (canvas: HTMLCanvasElement) => {
    const { width, height } = canvas;
    const isMobileLayout = window.innerWidth < window.innerHeight;
    return {
      cx: width / 2,
      cy: height / 2,
      rx: isMobileLayout ? width * 0.29 : width * 0.3,
      ry: isMobileLayout ? height * 0.35 : height * 0.4,
      width,
      height,
    };
  };

  // ─── Color helpers ───────────────────────────────────────────────────────
  const CYAN = (a: number) => `rgba(255, 255, 255, ${a})`;
  const GREEN = (a: number) => `rgba(50, 240, 145, ${a})`;

  // ─── Facial feature marker (L-corner reticle + label) ────────────────────
  const _drawFeatureMarker = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    feature: (typeof SKIN_FEATURES)[number],
    age: number,
    done: boolean
  ) => {
    const bx = cx + feature.ox * rx;
    const by = cy + feature.oy * ry;
    const bw = feature.rw * rx;
    const bh = feature.rh * ry;
    const arm = Math.min(bw, bh) * 0.28;

    // Brief flash on appearance then settle
    const flash = Math.max(0, 1 - age * 5) * 0.55;
    const steady = Math.min(age * 4, 1) * (done ? 0.8 : 0.55);
    const alpha = Math.min(flash + steady, 0.9);
    if (alpha <= 0) return;

    const col = done ? GREEN(alpha) : CYAN(alpha);

    ctx.save();

    // Subtle fill on flash
    if (flash > 0.02) {
      ctx.fillStyle = done ? GREEN(flash * 0.14) : CYAN(flash * 0.1);
      ctx.fillRect(bx - bw / 2, by - bh / 2, bw, bh);
    }

    // L-shaped corner marks
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    ctx.lineCap = 'square';
    ctx.setLineDash([]);
    const corners = [
      { x: bx - bw / 2, y: by - bh / 2, sx: 1, sy: 1 },
      { x: bx + bw / 2, y: by - bh / 2, sx: -1, sy: 1 },
      { x: bx - bw / 2, y: by + bh / 2, sx: 1, sy: -1 },
      { x: bx + bw / 2, y: by + bh / 2, sx: -1, sy: -1 },
    ];
    for (const { x, y, sx, sy } of corners) {
      ctx.beginPath();
      ctx.moveTo(x + sx * arm, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y + sy * arm);
      ctx.stroke();
    }

    // Small label in monospace — left-side features label left, others label right
    if (feature.label && alpha > 0.22) {
      const labelAlpha = Math.min((alpha - 0.22) / 0.2, 1);
      ctx.font = '9px monospace';
      ctx.fillStyle = done ? GREEN(labelAlpha * 0.9) : CYAN(labelAlpha * 0.8);
      ctx.textBaseline = 'bottom';
      if (feature.ox < -0.05) {
        ctx.textAlign = 'right';
        ctx.fillText(
          feature.label.toUpperCase(),
          bx - bw / 2 - 3,
          by - bh / 2 + 1
        );
      } else {
        ctx.textAlign = 'left';
        ctx.fillText(
          feature.label.toUpperCase(),
          bx + bw / 2 + 3,
          by - bh / 2 + 1
        );
      }
    }

    ctx.restore();
  };

  // ─── Complete scan frame (minimalist OCR style) ──────────────────────────
  const drawScanFrame = (canvas: HTMLCanvasElement, progress: number) => {
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { cx, cy, rx, ry, width, height } = getOvalParams(canvas);

      ctx.clearRect(0, 0, width, height);

      const done = progress >= 0.92;
      const doneT = done ? Math.min((progress - 0.92) / 0.08, 1) : 0;

      // Single top-to-bottom sweep (0.04 → 0.92)
      const scanFrac =
        progress < 0.04 ? 0 : Math.min((progress - 0.04) / 0.88, 1);
      const scanY = cy - ry + scanFrac * ry * 2;

      // --- Dark vignette outside oval ---
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.50)';
      ctx.beginPath();
      ctx.rect(0, 0, width, height);
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill('evenodd');
      ctx.restore();

      // --- Scanned region: subtle brightness above the line ---
      if (!done && scanFrac > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.clip();
        const scannedH = scanY - (cy - ry);
        const tint = ctx.createLinearGradient(cx, cy - ry, cx, scanY);
        tint.addColorStop(0, 'rgba(210, 225, 255, 0.09)');
        tint.addColorStop(1, 'rgba(210, 225, 255, 0.03)');
        ctx.fillStyle = tint;
        ctx.fillRect(cx - rx, cy - ry, rx * 2, scannedH);
        ctx.restore();
      }

      // --- Oval border ---
      ctx.save();
      ctx.strokeStyle = done
        ? GREEN(0.85 + doneT * 0.15)
        : CYAN(0.55 + scanFrac * 0.35);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.restore();

      // TODO: To revisit once facial features will be implemented
      // eslint-disable-next-line no-constant-condition
      /* --- Facial feature markers (appear as scan line passes each region) ---
      for (const feature of SKIN_FEATURES) {
        if (progress >= feature.scanT) {
          const age = Math.min((progress - feature.scanT) / 0.10, 1);
          drawFeatureMarker(ctx, cx, cy, rx, ry, feature, age, done);
        }
      }
      */

      // --- Scan beam ---
      if (!done && scanFrac > 0 && scanFrac < 1) {
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.clip();

        // Narrow soft trail above the line
        const trailH = 36;
        const trail = ctx.createLinearGradient(cx, scanY - trailH, cx, scanY);
        trail.addColorStop(0, 'rgba(255,255,255,0)');
        trail.addColorStop(1, 'rgba(255,255,255,0.09)');
        ctx.fillStyle = trail;
        ctx.fillRect(cx - rx, scanY - trailH, rx * 2, trailH);

        // Main scan line — fades at both horizontal edges
        const lineGrad = ctx.createLinearGradient(cx - rx, 0, cx + rx, 0);
        lineGrad.addColorStop(0, 'rgba(255,255,255,0)');
        lineGrad.addColorStop(0.12, 'rgba(255,255,255,0.85)');
        lineGrad.addColorStop(0.5, 'rgba(255,255,255,1)');
        lineGrad.addColorStop(0.88, 'rgba(255,255,255,0.85)');
        lineGrad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = lineGrad;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(cx - rx, scanY);
        ctx.lineTo(cx + rx, scanY);
        ctx.stroke();

        // Thin leading-edge glow below the line
        const glowH = 10;
        const glow = ctx.createLinearGradient(cx, scanY, cx, scanY + glowH);
        glow.addColorStop(0, 'rgba(255,255,255,0.16)');
        glow.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(cx - rx, scanY, rx * 2, glowH);

        ctx.restore();
      }

      // --- Completion: very soft green wash ---
      if (doneT > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = GREEN(doneT * 0.1);
        ctx.fillRect(cx - rx, cy - ry, rx * 2, ry * 2);
        ctx.restore();
      }
    } catch {
      // Draw failed silently
    }
  };

  // ─── Live-view overlay — colour reflects face detection state ────────────
  //  'none'     → white dashed oval    (no face detected)
  //  'detected' → amber dashed oval    (face found but not centred in oval)
  //  'ready'    → green solid oval     (face centred — ready to capture)
  const drawLiveOval = (
    canvas: HTMLCanvasElement,
    state: 'none' | 'detected' | 'ready'
  ) => {
    try {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const { cx, cy, rx, ry, width, height } = getOvalParams(canvas);

      ctx.clearRect(0, 0, width, height);

      // Dark vignette outside oval
      ctx.save();
      ctx.fillStyle = 'rgba(0,0,0,0.50)';
      ctx.beginPath();
      ctx.rect(0, 0, width, height);
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill('evenodd');
      ctx.restore();

      // Pick border colour + dash pattern based on state
      const borderColor =
        state === 'ready'
          ? 'rgba(50, 240, 145, 0.92)'
          : state === 'detected'
            ? 'rgba(255, 200, 50, 0.88)'
            : 'rgba(255, 255, 255, 0.65)';
      const glowColor =
        state === 'ready'
          ? 'rgba(50, 240, 145, 0.20)'
          : state === 'detected'
            ? 'rgba(255, 200, 50, 0.16)'
            : 'rgba(255, 255, 255, 0.08)';
      const dash = state === 'ready' ? [] : [8, 6];

      ctx.save();
      // Outer glow ring (only when a face is present)
      if (state !== 'none') {
        ctx.strokeStyle = glowColor;
        ctx.lineWidth = 14;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx + 5, ry + 5, 0, 0, 2 * Math.PI);
        ctx.stroke();
      }
      // Main oval border
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1.5;
      ctx.setLineDash(dash);
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.restore();
    } catch {
      // Draw failed silently
    }
  };

  // ─── Promise-based scan animation (triggered only on Capture) ────────────
  const runScanAnimation = (): Promise<void> =>
    new Promise((resolve) => {
      if (scanAnimRef.current !== null)
        cancelAnimationFrame(scanAnimRef.current);
      const canvas = overlayCanvasRef.current;
      if (!canvas) {
        resolve();
        return;
      }

      lastPctRef.current = -1;
      setScanPercent(0);

      const DURATION = 7000; // deliberate, document-scanner pace
      const start = performance.now();

      const tick = (now: number) => {
        const progress = Math.min((now - start) / DURATION, 1);
        drawScanFrame(canvas, progress);

        // Update percentage counter without flooding React with every frame
        const pct = Math.round(progress * 100);
        if (pct !== lastPctRef.current) {
          lastPctRef.current = pct;
          setScanPercent(pct);
        }

        if (progress < 1) {
          scanAnimRef.current = requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };

      scanAnimRef.current = requestAnimationFrame(tick);
    });

  // Request camera permissions on component mount
  useEffect(() => {
    let mounted = true;

    const initializeCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: 'user',
          },
        });

        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        setHasPermission(true);
        streamRef.current = stream;

        // Create a new video element
        const video = document.createElement('video');
        video.autoplay = true;
        video.playsInline = true;
        video.muted = true;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.objectFit = 'cover';
        video.style.transform = 'scaleX(-1)';
        video.style.backgroundColor = colors.text;

        // Wait for the video to be ready
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            resolve(true);
          };
          video.srcObject = stream;
        });

        // Start playing
        try {
          await video.play();

          // Now that video is playing, update the ref
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();

            // Set up overlay canvas
            if (overlayCanvasRef.current) {
              overlayCanvasRef.current.width = video.videoWidth;
              overlayCanvasRef.current.height = video.videoHeight;
            }
          }
        } catch {
          // Video play failed
        }

        await tensorFlowService.initialize();
        await faceDetectionService.initialize();
        startSkinToneAnalysis();
        startFaceDetection();
        if (overlayCanvasRef.current) {
          drawLiveOval(overlayCanvasRef.current, 'none');
        }
      } catch {
        setHasPermission(false);
      }
    };

    initializeCamera();

    return () => {
      mounted = false;
      if (analysisInterval.current) {
        clearInterval(analysisInterval.current);
      }
      if (faceDetectionInterval.current) {
        clearInterval(faceDetectionInterval.current);
      }
      if (scanAnimRef.current !== null) {
        cancelAnimationFrame(scanAnimRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init on mount only; deps are stable refs/callbacks
  }, []);

  function startSkinToneAnalysis() {
    if (analysisInterval.current) {
      clearInterval(analysisInterval.current);
    }

    // Wait a bit before starting analysis to ensure video is ready
    setTimeout(() => {
      analysisInterval.current = setInterval(async () => {
        if (
          videoRef.current &&
          canvasRef.current &&
          tensorFlowService.isReady() &&
          videoRef.current.videoWidth > 0 &&
          videoRef.current.videoHeight > 0
        ) {
          try {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');

            if (!ctx) return;

            // Set canvas dimensions to match video
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Mirror the video when drawing to canvas
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform

            // Only show a skin colour when a face is actually in the oval
            // (avoids showing colour for walls, phone screens, etc.)
            if (!isFaceDetectedRef.current || !isFaceInOvalRef.current) {
              setCurrentSkinTone(null);
              return;
            }
            const skinTone = await performEnhancedSkinToneAnalysis(canvas, ctx);
            setCurrentSkinTone(skinTone);
          } catch {
            // Analysis failed silently
          }
        }
      }, 100);
    }, 100);
  }

  function startFaceDetection() {
    if (faceDetectionInterval.current) {
      clearInterval(faceDetectionInterval.current);
    }

    setTimeout(() => {
      faceDetectionInterval.current = setInterval(async () => {
        // Skip detection while the scan animation is running
        if (scanAnimRef.current !== null) return;

        if (
          videoRef.current &&
          canvasRef.current &&
          videoRef.current.videoWidth > 0 &&
          videoRef.current.videoHeight > 0
        ) {
          try {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            ctx.setTransform(1, 0, 0, 1, 0, 0);

            let detected = false;
            let boundingBox:
              | { x: number; y: number; width: number; height: number }
              | undefined;

            // ── Primary: native browser FaceDetector (Chrome / Edge) ────────
            if ('FaceDetector' in window) {
              try {
                type NativeFace = {
                  boundingBox: {
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                  };
                };
                type NativeFD = {
                  detect(src: ImageBitmapSource): Promise<NativeFace[]>;
                };
                const FD = (
                  window as unknown as {
                    FaceDetector: new (o?: object) => NativeFD;
                  }
                ).FaceDetector;
                const detector = new FD({
                  maxDetectedFaces: 1,
                  fastMode: true,
                });
                const bitmap = await createImageBitmap(canvas);
                const faces = await detector.detect(bitmap);
                bitmap.close();

                detected = faces.length > 0;
                boundingBox = faces[0]?.boundingBox;
              } catch {
                // Native detector unavailable — fall through to service
              }
            }

            // ── Fallback: pixel-based service ────────────────────────────────
            if (!detected && !('FaceDetector' in window)) {
              const imageData = ctx.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );
              const faceResult =
                await faceDetectionService.detectFace(imageData);
              setFaceDetection(faceResult);
              detected = faceResult.detected;
              boundingBox = faceResult.boundingBox;

              if (faceResult.faceQuality) {
                setFaceQuality({
                  brightness: faceResult.faceQuality.brightness,
                  contrast: faceResult.faceQuality.contrast,
                  sharpness: faceResult.faceQuality.sharpness,
                  overall: faceResult.faceQuality.overall,
                });
              }
            }

            setIsFaceDetected(detected);

            const isInOval =
              detected && boundingBox
                ? checkFaceInOval(boundingBox, canvas.width, canvas.height)
                : false;
            setIsFaceInOval(isInOval);
          } catch {
            // Face detection tick failed silently
          }
        }
      }, 500);
    }, 1000);
  }

  const performEnhancedSkinToneAnalysis = async (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ) => {
    const { width, height } = canvas;
    const cx = width / 2;
    const cy = height / 2;

    // Match the oval dimensions drawn by drawFaceGuide exactly
    const isMobileLayout = window.innerWidth < window.innerHeight;
    const rx = isMobileLayout ? width * 0.29 : width * 0.3;
    const ry = isMobileLayout ? height * 0.35 : height * 0.4;

    // Read the bounding box of the oval in one efficient call
    const x0 = Math.max(0, Math.floor(cx - rx));
    const y0 = Math.max(0, Math.floor(cy - ry));
    const bw = Math.min(width - x0, Math.ceil(rx * 2));
    const bh = Math.min(height - y0, Math.ceil(ry * 2));

    const imageData = ctx.getImageData(x0, y0, bw, bh);
    const pixels = imageData.data;

    const skinRs: number[] = [];
    const skinGs: number[] = [];
    const skinBs: number[] = [];

    // Sample every other pixel for performance while covering the full oval
    for (let row = 0; row < bh; row += 2) {
      for (let col = 0; col < bw; col += 2) {
        const px = x0 + col;
        const py = y0 + row;

        // Reject pixels outside the oval
        const norm = Math.pow((px - cx) / rx, 2) + Math.pow((py - cy) / ry, 2);
        if (norm > 1) continue;

        const i = (row * bw + col) * 4;
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const a = pixels[i + 3];

        if (a < 128) continue;

        // Accept only pixels that look like skin (excludes hair, clothing, bg)
        const hsv = RGBtoHSV(r, g, b);
        const looksLikeSkin =
          hsv.s >= 0.05 &&
          hsv.s <= 0.88 &&
          hsv.v >= 0.15 &&
          hsv.v <= 0.98 &&
          ((hsv.h >= 0 && hsv.h <= 65) || hsv.h >= 330);

        if (looksLikeSkin) {
          skinRs.push(r);
          skinGs.push(g);
          skinBs.push(b);
        }
      }
    }

    // If too few skin pixels were found, fall back to all oval pixels
    let finalRs = skinRs;
    let finalGs = skinGs;
    let finalBs = skinBs;

    if (skinRs.length < 50) {
      finalRs = [];
      finalGs = [];
      finalBs = [];
      for (let row = 0; row < bh; row += 2) {
        for (let col = 0; col < bw; col += 2) {
          const px = x0 + col;
          const py = y0 + row;
          const norm =
            Math.pow((px - cx) / rx, 2) + Math.pow((py - cy) / ry, 2);
          if (norm > 1) continue;
          const i = (row * bw + col) * 4;
          if (pixels[i + 3] < 128) continue;
          finalRs.push(pixels[i]);
          finalGs.push(pixels[i + 1]);
          finalBs.push(pixels[i + 2]);
        }
      }
    }

    if (finalRs.length === 0) {
      return tensorFlowService.analyzeSkinTone(
        new Uint8ClampedArray(0),
        width,
        height
      );
    }

    // Median is robust against outliers (hair, background edges, specular highlights)
    const median = (arr: number[]) => {
      const sorted = [...arr].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? (sorted[mid - 1]! + sorted[mid]!) / 2
        : sorted[mid]!;
    };

    const medR = Math.round(median(finalRs));
    const medG = Math.round(median(finalGs));
    const medB = Math.round(median(finalBs));

    return tensorFlowService.analyzeSkinTone(
      new Uint8ClampedArray([medR, medG, medB]),
      width,
      height
    );
  };

  const checkFaceInOval = (
    boundingBox: { x: number; y: number; width: number; height: number },
    canvasWidth: number,
    canvasHeight: number
  ): boolean => {
    const faceCenterX = boundingBox.x + boundingBox.width / 2;
    const faceCenterY = boundingBox.y + boundingBox.height / 2;

    // Oval center and radii
    const cx = canvasWidth / 2;
    const cy = canvasHeight / 2;
    const rx = isMobile ? canvasWidth * 0.38 : canvasWidth * 0.3;
    const ry = isMobile ? canvasHeight * 0.35 : canvasHeight * 0.4;

    // Check if face center is inside the oval
    const norm =
      Math.pow(faceCenterX - cx, 2) / Math.pow(rx, 2) +
      Math.pow(faceCenterY - cy, 2) / Math.pow(ry, 2);

    return norm <= 1;
  };

  // ─── Keep the live oval in sync with face-detection state ───────────────
  // Runs whenever face presence / position changes AND we are not scanning.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (scanPhase !== 'idle') return;
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const state: 'none' | 'detected' | 'ready' = !isFaceDetected
      ? 'none'
      : isFaceInOval
        ? 'ready'
        : 'detected';
    drawLiveOval(canvas, state);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- drawLiveOval is stable; avoid redraw loops
  }, [isFaceDetected, isFaceInOval, scanPhase]);

  // Check if face is properly positioned for capture
  const canCapture = isFaceDetected && isFaceInOval;

  // Handle cancel button click with tracking
  const handleCancel = () => {
    trackButtonClick('Cancel', 'camera-component');

    // Call the original onClose callback
    onClose();
  };

  /**
   * Validates that a real human face is present in the oval at capture time.
   *
   * - When FaceDetector API is available (Chrome/Edge): use it only. Never
   *   fall back to skin color — if the API throws or returns 0 faces, we
   *   return invalid. Skin color alone cannot distinguish a face from a phone
   *   screen or other warm-coloured object.
   * - When FaceDetector is not available (Firefox, Safari): use strict
   *   pixel-based checks; we still cannot reliably reject phone screens there.
   */
  const validateFacePresence = async (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ): Promise<{ valid: boolean; reason: string }> => {
    try {
      const { width, height } = canvas;
      if (width === 0 || height === 0) {
        return {
          valid: false,
          reason: 'Camera frame not ready. Please try again.',
        };
      }

      const cx = width / 2;
      const cy = height / 2;
      const isMobileLayout = window.innerWidth < window.innerHeight;
      const rx = isMobileLayout ? width * 0.29 : width * 0.3;
      const ry = isMobileLayout ? height * 0.35 : height * 0.4;

      // ── When FaceDetector is available: use it exclusively ──────────────────
      // Do not fall back to skin color when it throws — that would accept
      // phone screens and other non-faces. Fail closed.
      if (typeof window !== 'undefined' && 'FaceDetector' in window) {
        type NativeFace = {
          boundingBox: { x: number; y: number; width: number; height: number };
        };
        type NativeFaceDetector = {
          detect(src: ImageBitmapSource): Promise<NativeFace[]>;
        };
        const FD = (
          window as unknown as {
            FaceDetector: new (o?: object) => NativeFaceDetector;
          }
        ).FaceDetector;

        let bitmap: ImageBitmap;
        try {
          bitmap = await createImageBitmap(canvas);
        } catch {
          return {
            valid: false,
            reason: 'Could not read camera frame. Please try again.',
          };
        }

        try {
          const detector = new FD({ maxDetectedFaces: 1, fastMode: false });
          const faces = await detector.detect(bitmap);
          bitmap.close();

          if (faces.length === 0) {
            return {
              valid: false,
              reason:
                'No face detected. Please position your face within the oval.',
            };
          }

          const inOval = faces.some((f) => {
            const fx = f.boundingBox.x + f.boundingBox.width / 2;
            const fy = f.boundingBox.y + f.boundingBox.height / 2;
            const nx = (fx - cx) / rx;
            const ny = (fy - cy) / ry;
            return nx * nx + ny * ny <= 1.3;
          });

          if (!inOval) {
            return {
              valid: false,
              reason:
                'Face not centred in the oval. Please adjust your position.',
            };
          }

          return { valid: true, reason: '' };
        } catch {
          bitmap.close();
          return {
            valid: false,
            reason:
              'Face detection failed. Please try again with your face clearly in view.',
          };
        }
      }

      // ── Fallback only when FaceDetector is not in the browser ─────────────
      // (e.g. Firefox, Safari). Pixel checks cannot reliably reject phone
      // screens; thresholds are strict to minimise false positives.
      const x0 = Math.max(0, Math.floor(cx - rx));
      const y0 = Math.max(0, Math.floor(cy - ry));
      const bw = Math.min(width - x0, Math.ceil(rx * 2));
      const bh = Math.min(height - y0, Math.ceil(ry * 2));

      const { data: pixels } = ctx.getImageData(x0, y0, bw, bh);

      let ovalPixels = 0,
        skinPixels = 0;
      let skinSumNx = 0,
        skinSumNy = 0;
      let lumSum = 0,
        lumSumSq = 0;

      for (let row = 0; row < bh; row += 2) {
        for (let col = 0; col < bw; col += 2) {
          const px = x0 + col;
          const py = y0 + row;
          const nx = (px - cx) / rx;
          const ny = (py - cy) / ry;
          if (nx * nx + ny * ny > 1) continue;

          const i = (row * bw + col) * 4;
          if (pixels[i + 3] < 128) continue;

          ovalPixels++;

          const r = pixels[i],
            g = pixels[i + 1],
            b = pixels[i + 2];
          const lum = 0.299 * r + 0.587 * g + 0.114 * b;
          lumSum += lum;
          lumSumSq += lum * lum;

          const hsv = RGBtoHSV(r, g, b);
          const isSkin =
            hsv.s >= 0.12 &&
            hsv.s <= 0.8 &&
            hsv.v >= 0.2 &&
            hsv.v <= 0.97 &&
            ((hsv.h >= 0 && hsv.h <= 50) || hsv.h >= 340);

          if (isSkin) {
            skinPixels++;
            skinSumNx += nx;
            skinSumNy += ny;
          }
        }
      }

      if (ovalPixels < 100) {
        return {
          valid: false,
          reason: 'Unable to read camera frame. Please try again.',
        };
      }

      // A. Skin density
      if (skinPixels / ovalPixels < 0.15) {
        return {
          valid: false,
          reason:
            'No face detected. Please position your face within the oval.',
        };
      }

      // B. Skin centroid
      const centDist = Math.sqrt(
        Math.pow(skinSumNx / skinPixels, 2) +
          Math.pow(skinSumNy / skinPixels, 2)
      );
      if (centDist > 0.6) {
        return {
          valid: false,
          reason: 'Face not centred. Please move your face into the oval.',
        };
      }

      // C. Brightness variance
      const meanLum = lumSum / ovalPixels;
      const stdDev = Math.sqrt(
        Math.max(0, lumSumSq / ovalPixels - meanLum * meanLum)
      );
      if (stdDev < 12) {
        return {
          valid: false,
          reason:
            'No face detected. Ensure good lighting and centre your face in the oval.',
        };
      }

      return { valid: true, reason: '' };
    } catch {
      return {
        valid: false,
        reason: 'Could not read camera frame. Please try again.',
      };
    }
  };

  const handleCapture = async (e?: GestureResponderEvent) => {
    e?.stopPropagation?.();

    trackAnalyticsEvent({
      event: 'button_click',
      button_name: 'Capture',
      button_location: 'camera-component',
      face_detected: isFaceDetected,
      face_in_oval: isFaceInOval,
      can_capture: canCapture,
      capture_oval_only: captureOvalOnly,
    });

    if (videoRef.current && canvasRef.current) {
      try {
        // Disable the button immediately while we validate
        setIsProcessing(true);

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          setIsProcessing(false);
          return;
        }

        // Freeze the current frame so we can inspect it
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        ctx.setTransform(1, 0, 0, 1, 0, 0);

        // ── Face gate ──────────────────────────────────────────────────────
        // Must pass BEFORE we switch to scanning phase so the scan UI never
        // activates when no face is present.
        const faceCheck = await validateFacePresence(canvas, ctx);
        if (!faceCheck.valid) {
          setCaptureError(faceCheck.reason);
          setIsProcessing(false);
          // scanPhase stays 'idle' — scanning UI never shown
          if (overlayCanvasRef.current)
            drawLiveOval(overlayCanvasRef.current, 'none');
          setTimeout(() => setCaptureError(null), 4000);
          return;
        }

        // Face confirmed — now enter scanning phase
        setScanPhase('scanning');

        // Run the visual scan animation and skin-tone analysis in parallel.
        // The animation takes ~2.6 s which gives the analysis plenty of time.
        const [enhancedSkinTone] = await Promise.all([
          performEnhancedSkinToneAnalysis(canvas, ctx),
          runScanAnimation(),
        ]);

        setScanPhase('complete');
        setCurrentSkinTone(enhancedSkinTone);

        // Brief "complete" moment so the user sees the green flash
        await new Promise<void>((r) => setTimeout(r, 500));

        // Restore live oval for the brief moment before navigation
        if (overlayCanvasRef.current)
          drawLiveOval(overlayCanvasRef.current, 'ready');

        // Create an oval-cropped canvas that matches the capture guide when requested
        let outputCanvas: HTMLCanvasElement = canvas;
        if (captureOvalOnly) {
          const cropCanvas = document.createElement('canvas');
          const cropCtx = cropCanvas.getContext('2d');

          if (cropCtx) {
            const canvasWidth = canvas.width;
            const canvasHeight = canvas.height;
            const cx = canvasWidth / 2;
            const cy = canvasHeight / 2;
            const rx = isMobile ? canvasWidth * 0.38 : canvasWidth * 0.3;
            const ry = isMobile ? canvasHeight * 0.35 : canvasHeight * 0.4;
            const cropWidth = Math.max(2, rx * 2);
            const cropHeight = Math.max(2, ry * 2);

            cropCanvas.width = cropWidth;
            cropCanvas.height = cropHeight;

            cropCtx.save();
            cropCtx.beginPath();
            cropCtx.ellipse(
              cropWidth / 2,
              cropHeight / 2,
              cropWidth / 2,
              cropHeight / 2,
              0,
              0,
              Math.PI * 2
            );
            cropCtx.closePath();
            cropCtx.clip();

            cropCtx.drawImage(
              canvas,
              cx - cropWidth / 2,
              cy - cropHeight / 2,
              cropWidth,
              cropHeight,
              0,
              0,
              cropWidth,
              cropHeight
            );
            cropCtx.restore();

            outputCanvas = cropCanvas;
          }
        }

        // Convert to blob
        const blob = await new Promise<Blob>((resolve) => {
          outputCanvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
            },
            captureOvalOnly ? 'image/png' : 'image/jpeg',
            captureOvalOnly ? 0.95 : 0.8
          );
        });

        const uri = URL.createObjectURL(blob);
        trackAnalyticsEvent({
          event: 'image_captured',
          location: 'camera-component',
          has_skin_tone: !!enhancedSkinTone,
          skin_tone_category: enhancedSkinTone?.category || 'unknown',
          skin_tone_confidence: enhancedSkinTone?.confidence || 0,
          capture_oval_only: captureOvalOnly,
        });

        // Collect the unique feature labels that the scan marked during animation
        const detectedFeatureLabels = SKIN_FEATURES.map((f) => f.label).filter(
          (v, i, a) => v !== '' && a.indexOf(v) === i
        );

        onImageCaptured(
          uri,
          enhancedSkinTone || undefined,
          detectedFeatureLabels
        );
      } catch {
        Alert.alert('Error', 'Failed to take picture. Please try again.');
        if (overlayCanvasRef.current)
          drawLiveOval(overlayCanvasRef.current, 'none');
      } finally {
        setIsProcessing(false);
        setScanPhase('idle');
      }
    }
  };

  // Loading state while checking permissions
  if (hasPermission === null) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  // Error state when camera permission is denied
  if (hasPermission === false) {
    return (
      <View style={styles.container}>
        <Text style={styles.text}>No access to camera</Text>
        <Button
          title="Close"
          onPress={onClose}
          variant="primary"
          style={styles.button}
        />
      </View>
    );
  }

  // Main camera view with controls
  return (
    <View style={styles.container}>
      {/* Face-positioning hint — hidden while scanning */}
      {isFaceDetected && !isFaceInOval && scanPhase === 'idle' && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            alignItems: 'center',
            zIndex: 1001,
            pointerEvents: 'none',
          }}
        >
          <Text
            style={{
              color: colors.background,
              backgroundColor: 'rgba(255,0,0,0.7)',
              textAlign: 'center',
              padding: 8,
              borderRadius: 8,
              maxWidth: 400,
              width: '80%',
              alignSelf: 'center',
              marginTop: 16,
            }}
          >
            Please center your face within the oval to enable capture.
          </Text>
        </View>
      )}

      {/* Camera viewport + scan status overlay — all contained within the same wrapper
          so the overlay never escapes the camera area or overlaps the button row. */}
      <View style={styles.cameraViewWrapper}>
        <View style={styles.cameraView}>
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              backgroundColor: colors.text,
            }}
            autoPlay
            playsInline
            muted
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          <canvas
            ref={overlayCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              display: 'block',
            }}
          />
        </View>

        {/* Capture error — pinned to the top edge of the camera view */}
        {captureError && (
          <View
            style={{
              position: 'absolute',
              top: 12,
              left: 0,
              right: 0,
              alignItems: 'center',
              zIndex: 1003,
              pointerEvents: 'none',
            }}
          >
            <View
              style={{
                backgroundColor: 'rgba(180,30,30,0.88)',
                borderRadius: 10,
                paddingHorizontal: 16,
                paddingVertical: 9,
                maxWidth: '88%',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Text
                style={{
                  color: '#fff',
                  fontSize: 13,
                  fontWeight: '600',
                  flexShrink: 1,
                  lineHeight: 18,
                }}
              >
                {captureError}
              </Text>
            </View>
          </View>
        )}

        {/* Scan status — pinned to the bottom edge of the camera view */}
        {scanPhase !== 'idle' && (
          <View
            style={{
              position: 'absolute',
              bottom: 14,
              left: 0,
              right: 0,
              alignItems: 'center',
              zIndex: 1002,
              pointerEvents: 'none',
            }}
          >
            <View
              style={{
                backgroundColor: 'rgba(0,0,0,0.68)',
                borderRadius: 12,
                paddingHorizontal: 18,
                paddingVertical: 10,
                alignItems: 'center',
                maxWidth: '90%',
                gap: 5,
              }}
            >
              {scanPhase === 'scanning' ? (
                <>
                  {/* Percentage + zone on one row */}
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'baseline',
                      gap: 6,
                    }}
                  >
                    <Text
                      style={{
                        color: '#fff',
                        fontSize: 22,
                        fontWeight: '700',
                        fontVariant: ['tabular-nums'],
                      }}
                    >
                      {String(scanPercent).padStart(3, '\u00A0')}
                      <Text
                        style={{
                          color: 'rgba(255,255,255,0.45)',
                          fontSize: 13,
                          fontWeight: '500',
                        }}
                      >
                        %
                      </Text>
                    </Text>
                    <Text
                      style={{
                        color: 'rgba(255,255,255,0.50)',
                        fontSize: 9,
                        letterSpacing: 1.2,
                        textTransform: 'uppercase',
                        flexShrink: 1,
                      }}
                    >
                      {getScanZoneLabel(scanPercent)}
                    </Text>
                  </View>

                  {/* Thin progress bar */}
                  <View
                    style={{
                      width: 140,
                      height: 2,
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      borderRadius: 1,
                    }}
                  >
                    <View
                      style={{
                        width: `${scanPercent}%` as unknown as number,
                        height: 2,
                        backgroundColor: 'rgba(255,255,255,0.60)',
                        borderRadius: 1,
                      }}
                    />
                  </View>

                  {/* TODO: To revisit once facial features will be implemented */}
                  {/* eslint-disable-next-line no-constant-condition */}
                  {/* Feature chips — commented out until real facial feature detection is in place
                  {detectedFeatures.length > 0 && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, justifyContent: 'center', marginTop: 2 }}>
                      {detectedFeatures.map(feat => (
                        <View key={feat} style={{ backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2 }}>
                          <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 7, letterSpacing: 0.9, textTransform: 'uppercase' }}>
                            {feat}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                  */}
                </>
              ) : (
                <Text
                  style={{
                    color: '#50f096',
                    fontSize: 13,
                    fontWeight: '700',
                    letterSpacing: 0.4,
                  }}
                >
                  ✓ Analysis complete
                </Text>

                // TODO: To revisit once facial features will be implemented
                // eslint-disable-next-line no-constant-condition
                /* Completion feature chips — commented out until real facial feature detection is in place
                <>
                  {detectedFeatures.length > 0 && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                      {detectedFeatures.map(feat => (
                        <View key={feat} style={{ backgroundColor: 'rgba(50,240,145,0.10)', borderRadius: 4, paddingHorizontal: 5, paddingVertical: 2, borderWidth: 1, borderColor: 'rgba(50,240,145,0.22)' }}>
                          <Text style={{ color: 'rgba(50,240,145,0.85)', fontSize: 7, letterSpacing: 0.9, textTransform: 'uppercase' }}>
                            {feat}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </>
                */
              )}
            </View>
          </View>
        )}
      </View>

      {/* Detected colour swatch — only shown after a successful idle scan */}
      {currentSkinTone && scanPhase === 'idle' && (
        <View>
          <View style={styles.skinToneOverlay}>
            <View style={styles.skinToneInfo}>
              <Text style={styles.skinToneText}>Detected colour:</Text>
              <View
                style={[
                  styles.colorSwatch,
                  { backgroundColor: currentSkinTone.color },
                ]}
              />
            </View>
          </View>
        </View>
      )}
      <View
        style={[styles.buttonContainer, { marginTop: !isMobile ? -50 : 0 }]}
      >
        <View
          style={[
            styles.controlsContainer,
            { marginTop: isMobile ? 0 : Platform.OS === 'ios' ? 60 : 50 },
            { marginBottom: isMobile ? 20 : 50 },
          ]}
        >
          <Button
            title="Capture"
            onPress={handleCapture}
            disabled={isProcessing || !canCapture}
            loading={isProcessing}
            style={styles.captureButton}
          />

          <Button
            title="Cancel"
            onPress={handleCancel}
            style={styles.closeButton}
            variant="outline"
            hoverColor={colors.authBackground}
          />
        </View>
      </View>
    </View>
  );
};

// Styles for the camera component
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  cameraViewWrapper: {
    flex: 1,
    aspectRatio: 3 / 4,
    alignSelf: 'center',
    width: '100%',
    maxWidth: '80%',
    maxHeight: '70%',
    position: 'relative',
  },
  cameraView: {
    flex: 1,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    borderRadius: 10,
  },
  buttonContainer: {
    backgroundColor: 'transparent',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    padding: 30,
    position: 'relative',
    width: '100%',
    zIndex: 999,
  },
  controlsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 10,
    alignItems: 'center',
    marginBottom: Platform.OS === 'ios' ? 60 : 50,
    width: '100%',
  },
  captureButton: {
    width: 100,
    height: 40,
  },
  closeButton: {
    width: 100,
    height: 40,
  },
  text: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 30,
  },
  button: {
    padding: 25,
    borderRadius: 12,
  },
  skinToneOverlay: {
    position: 'relative',
    top: 20,
    left: 20,
    right: 20,
    padding: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  skinToneInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  colorSwatch: {
    width: 30,
    height: 30,
    borderRadius: 15,
  },
  skinToneText: {
    fontSize: 16,
    fontWeight: 'bold',
  },
});
